import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {
	ACTION_BUTTON_SIZE,
	ActionButton,
	ButtonDropdown,
	COUNT_BADGE_PLACEMENT,
	CountBadge,
	DropdownList,
	EMPHASIS,
	FlexRow,
	Icon,
} from '@forecast-it/design-system';
import {AGENTIC_WORKERS_MESSAGE} from './agenticWorkersMessages';
import type {AgentDeclaration, AgentPage} from './agentDeclaration';
import {getAgentsForPage} from './agentRegistry';
import {
	AGENT_DRAWER,
	closeAgentDrawer,
	formatWaitingCount,
	openAgentDrawer,
	useAgentPageState,
	useOpenAgentDrawer,
	useRunFinishedToasts,
	type AgentDrawer,
} from './agentRunState';
import {canSeeAgent} from './agentVisibility';
import {ScheduleDrawer} from '../scheduling/ScheduleDrawer';
import {RightDrawer} from '../right-drawer/RightDrawer';
import {AgentStatusLine} from './AgentStatusLine';
import {AgentPastRuns} from './AgentPastRuns';
import {AgentErrorBoundary} from './AgentErrorBoundary';

const MENU_OFFSET_PIXELS = 4;
const CLOSE_MESSAGE = 'common.close';

export const agentControlDataCy = (agentId: string): string => `agent-button-${agentId}`;

// Focus that fell from an element taken away rests on the page's body, or on nothing at all.
const isFocusOnPage = (): boolean => document.activeElement === null || document.activeElement === document.body;

// Positioned so the waiting badge can sit on the button's top-right corner.
const AgentControl = styled(FlexRow)`
	position: relative;
`;

const MenuItemRow = ({children}: {children: React.ReactNode}): JSX.Element => (
	<FlexRow alignItems={'center'} gap={'s'}>
		{children}
	</FlexRow>
);

type AgentButtonProps = {
	declaration: AgentDeclaration;
};

// One agent's button, menu and drawers. A component of its own because each agent holds its own run state. The
// drawers follow the store's open drawer, so one is open at a time, across agents.
const AgentButton = ({declaration}: AgentButtonProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {isRunning, waitingCount, startRun, stopRun} = useAgentPageState(declaration);
	useRunFinishedToasts(declaration);
	// Each run asked for, the same run again too, is a new request, and the past runs start afresh for it: All, no
	// search, then that run in view, even after the agent threw. Choosing Past Runs again while it is open changes
	// nothing.
	const {drawer, focusRunId, focusRunRequest} = useOpenAgentDrawer(declaration.id);
	const [isMenuOpen, setMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const focusTriggerWhenIdleRef = useRef(false);

	const agentName = formatMessage(declaration.name);
	const pastRunsTitle = formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_PAST_RUNS}, {name: agentName});
	const waitingAccessibleName = formatWaitingCount(intl, declaration, waitingCount);

	const runFromMenu = (): void => {
		setMenuOpen(false);
		startRun();
	};

	const openDrawerFromMenu = (agentDrawer: AgentDrawer): void => {
		setMenuOpen(false);
		openAgentDrawer(declaration.id, agentDrawer);
	};

	const closeSchedule = (): void => {
		closeAgentDrawer(declaration.id, AGENT_DRAWER.SCHEDULE);
	};

	const closePastRuns = (): void => {
		closeAgentDrawer(declaration.id, AGENT_DRAWER.PAST_RUNS);
	};

	// The store keeps the open drawer, so leaving the page closes it, or it would open again on the way back.
	useEffect(
		() => () => {
			closeAgentDrawer(declaration.id, AGENT_DRAWER.SCHEDULE);
			closeAgentDrawer(declaration.id, AGENT_DRAWER.PAST_RUNS);
		},
		[declaration.id]
	);

	// The stop part disappears and the trigger becomes a different element once the run stops, so focus
	// is handed to the new trigger after the re-render instead of falling back to the page.
	useEffect(() => {
		const shouldFocusTrigger = !isRunning && focusTriggerWhenIdleRef.current;
		focusTriggerWhenIdleRef.current = false;
		if (shouldFocusTrigger) {
			triggerRef.current?.focus();
		}
	}, [isRunning]);

	// A drawer gives focus back to what opened it as it closes. Opened from the menu, that is gone by then, so the trigger
	// takes the focus that would fall to the page.
	const previousDrawerRef = useRef(drawer);
	useEffect(() => {
		const hadDrawerOpen = previousDrawerRef.current !== null;
		previousDrawerRef.current = drawer;
		if (hadDrawerOpen && drawer === null && isFocusOnPage()) {
			triggerRef.current?.focus();
		}
	}, [drawer]);

	const stopFromButton = (): void => {
		setMenuOpen(false);
		focusTriggerWhenIdleRef.current = true;
		stopRun();
	};

	return (
		<AgentControl flex={'0 0 auto'} data-cy={agentControlDataCy(declaration.id)}>
			<DropdownList open={isMenuOpen} onOpenChange={setMenuOpen}>
				<DropdownList.Trigger>
					{isRunning ? (
						<ActionButton
							ref={triggerRef}
							size={ACTION_BUTTON_SIZE.STANDARD}
							emphasis={EMPHASIS.MEDIUM}
							waiting={true}
							aria-label={formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING_NAME}, {name: agentName})}
							trailingAction={{
								icon: 'stop',
								'aria-label': formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_STOP}, {name: agentName}),
								onClick: stopFromButton,
								'data-cy': 'agent-button-stop',
							}}
							data-cy={'agent-button-trigger'}
						>
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.BUTTON_WORKING})}
						</ActionButton>
					) : (
						<ButtonDropdown
							ref={triggerRef}
							emphasis={EMPHASIS.MEDIUM}
							icon={'sparkle'}
							data-cy={'agent-button-trigger'}
						>
							{agentName}
						</ButtonDropdown>
					)}
				</DropdownList.Trigger>
				<DropdownList.Content align={'start'} side={'bottom'} offset={MENU_OFFSET_PIXELS} portalled={true}>
					<DropdownList.Item disabled={isRunning} onClick={runFromMenu} data-cy={'agent-menu-run'}>
						<MenuItemRow>
							<Icon icon={'sparkle'} size={'s'} newColor={isRunning ? 'disabled' : undefined} />
							{isRunning
								? formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUNNING})
								: formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_RUN}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item
						onClick={() => openDrawerFromMenu(AGENT_DRAWER.SCHEDULE)}
						data-cy={'agent-menu-schedule'}
					>
						<MenuItemRow>
							<Icon icon={'clock'} size={'s'} />
							{formatMessage({id: AGENTIC_WORKERS_MESSAGE.MENU_SCHEDULE}, {name: agentName})}
						</MenuItemRow>
					</DropdownList.Item>
					<DropdownList.Item
						onClick={() => openDrawerFromMenu(AGENT_DRAWER.PAST_RUNS)}
						data-cy={'agent-menu-past-runs'}
					>
						<MenuItemRow>
							<Icon icon={'list'} size={'s'} />
							{pastRunsTitle}
							<CountBadge
								count={waitingCount}
								aria-label={waitingAccessibleName}
								placement={COUNT_BADGE_PLACEMENT.INLINE}
							/>
						</MenuItemRow>
					</DropdownList.Item>
				</DropdownList.Content>
			</DropdownList>
			<CountBadge count={waitingCount} aria-label={waitingAccessibleName} placement={COUNT_BADGE_PLACEMENT.CORNER} />
			<AgentStatusLine declaration={declaration} />
			<ScheduleDrawer
				isOpen={drawer === AGENT_DRAWER.SCHEDULE}
				onClose={closeSchedule}
				name={agentName}
				consumer={declaration.schedulingTarget}
			/>
			<RightDrawer
				isOpen={drawer === AGENT_DRAWER.PAST_RUNS}
				title={pastRunsTitle}
				icon={'clock'}
				closeLabel={formatMessage({id: CLOSE_MESSAGE})}
				onClose={closePastRuns}
				data-cy={'past-runs-drawer'}
			>
				<AgentErrorBoundary key={focusRunRequest}>
					<AgentPastRuns declaration={declaration} focusRunId={focusRunId} onClose={closePastRuns} />
				</AgentErrorBoundary>
			</RightDrawer>
		</AgentControl>
	);
};

type AgentToolbarButtonsProps = {
	page: AgentPage;
};

export const AgentToolbarButtons = ({page}: AgentToolbarButtonsProps): JSX.Element | null => {
	const visibleAgents = getAgentsForPage(page).filter(canSeeAgent);
	if (visibleAgents.length === 0) {
		return null;
	}
	return (
		<FlexRow alignItems={'center'} gap={'s'} flex={'0 0 auto'} data-cy={'agent-toolbar-buttons'}>
			{visibleAgents.map(declaration => (
				<AgentButton key={declaration.id} declaration={declaration} />
			))}
		</FlexRow>
	);
};
